/*
 * Mural Studio page (sections/design-studio.liquid). Vanilla JS, no dependencies.
 *
 * Talks only to the same-origin app proxy /apps/studio/* (Projects/design-studio/service, routes/proxy.py):
 *   GET  api/status | GET/POST api/session | POST api/generate | GET api/jobs/<id> | GET api/designs
 *   POST api/designs/<id>/select | POST api/email
 * The session token lives in localStorage and goes out as the X-Studio-Session header (the proxy strips
 * cookies). Every generate needs a fresh Cloudflare Turnstile token (they are single-use).
 * The browser only ever receives watermarked JPEGs; images are not draggable and have no download links.
 *
 * No <form> elements on purpose: the site's Google tag auto-tracks form_start/form_submit on any <form>, and
 * studio clicks must never look like ad conversions (seen 2026-09-28 on the sandbox). Enter keys are wired by hand.
 *
 * URL parameters: ?idea=<text> prefills the description (the "Make This My Own" button uses it),
 * ?mode=mural is accepted, ?preview=<code> opens a preview session while the studio is in internal mode.
 *
 * Sign in (2026-09-28): "Sign in" goes to Shopify's customer-account sign-in and comes back here. The backend
 * learns who's signed in from the app proxy (never from this script), moves this browser's designs into the
 * account and says `signed_in`; a signed-in customer on a new device sees their designs without a session.
 */
(function () {
  "use strict";

  var root = document.querySelector("[data-ds-root]");
  var cfgEl = document.querySelector("[data-ds-config]");
  if (!root || !cfgEl) return;
  var cfg = {};
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  var API = cfg.api || "/apps/studio";

  var LOADING_LINES = ["Sketching your idea", "Mixing the colours", "Composing it for your wall", "Adding the details", "Almost there"];
  var LIMIT_TEXT = {
    email_required: "",
    limit_email_day: "You've made a lot of designs today. Come back tomorrow for more, or use one you've already made.",
    limit_email_month: "You've reached this month's design limit. Pick one of your designs below, or email us and we'll help.",
    limit_ip: "We've had a lot of designs from your network today. Please come back tomorrow.",
    busy: "The studio is busy for a moment. Please try again in a few seconds.",
    one_at_a_time: "We're still working on your last design. One moment.",
    no_session: "Your session expired. Please try again."
  };

  // Shown under the empty frame when the section has no "Idea" blocks yet (text only; blocks add a picture).
  var DEFAULT_IDEAS = [
    "Desert mesas at sunset, warm and minimal",
    "A calm lake below blue mountains, in Japanese ink",
    "Lemon trees against a pale Mediterranean sky",
    "Tropical leaves in deep greens, botanical print style"
  ];

  var LS_SESSION = "acStudio.session";
  var LS_PREVIEW = "acStudio.preview";
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode: session lasts this page */ } }
  function lsDel(k) { try { window.localStorage.removeItem(k); } catch (e) {} }

  var params = new URLSearchParams(window.location.search);
  var previewParam = params.get("preview");
  if (previewParam) { lsSet(LS_PREVIEW, previewParam); lsDel(LS_SESSION); }

  var state = {
    session: lsGet(LS_SESSION),
    previewCode: lsGet(LS_PREVIEW),
    freeLeft: null,
    hasEmail: false,
    signedIn: false,
    designs: [],
    current: null,
    busy: false,
    unit: "in",           // the editors' units toggle: "in" (default, Ross 2026-09-28) or "ftin"
    stripOffset: {},
    resting: false
  };

  // ------------------------------------------------------------------ helpers

  function el(tag, attrs, children) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === "class") n.className = attrs[k];
      else if (k === "text") n.textContent = attrs[k];
      else if (k.indexOf("on") === 0) n.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] !== null && attrs[k] !== undefined && attrs[k] !== false) n.setAttribute(k, attrs[k] === true ? "" : attrs[k]);
    });
    (children || []).forEach(function (c) { if (c) n.appendChild(typeof c === "string" ? document.createTextNode(c) : c); });
    return n;
  }
  function $(sel) { return root.querySelector(sel); }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function api(method, path, body) {
    var headers = { "Accept": "application/json" };
    if (body) headers["Content-Type"] = "application/json";
    if (state.session) headers["X-Studio-Session"] = state.session;
    return fetch(API + path, {
      method: method, headers: headers, body: body ? JSON.stringify(body) : undefined,
      credentials: "same-origin", cache: "no-store"
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) { return { status: r.status, data: data || {} }; });
    }).catch(function () { return { status: 0, data: {} }; });
  }

  // ------------------------------------------------------------------ Turnstile (a visible box, ticked BEFORE Create)
  // The Cloudflare box shows above the Create button and verifies on its own (most people never click); Create looks
  // locked until it has. A token is single-use, so each call that needs one (opening the session, each generate)
  // takes the ready token, then the box is reset and quietly verifies again for the next call.

  var ts = { widget: null, token: null, waiters: [], unavailable: false, timer: null };

  function waitForTurnstile() {
    return new Promise(function (resolve) {
      var tries = 0;
      (function check() {
        if (window.turnstile) return resolve(window.turnstile);
        if (++tries > 100) return resolve(null);
        setTimeout(check, 100);
      })();
    });
  }

  function turnstileReady() {       // true when Create may be pressed: a token is ready, or there is nothing to wait for
    return !cfg.turnstileSiteKey || !!ts.token || ts.unavailable;
  }

  function updateGate() {
    var btn = $(".ds-create");
    if (!btn) return;
    var locked = !turnstileReady();
    btn.classList.toggle("is-locked", locked);
    if (locked) btn.setAttribute("aria-disabled", "true"); else btn.removeAttribute("aria-disabled");
    var box = $(".ds-turnstile");
    if (box) box.classList.toggle("is-verified", !!ts.token);
  }

  function gotToken(token) {
    ts.token = token || null;
    clearTimeout(ts.timer);
    humanHint(false);
    var ask = $(".ds-notice[data-verify]"); if (ask) ask.remove();
    if (ts.token && ts.waiters.length) {           // someone was already waiting for one: hand it over
      var w = ts.waiters.shift();
      ts.token = null;
      clearTimeout(w.timer);
      w.resolve(token);
      if (ts.widget !== null && window.turnstile) window.turnstile.reset(ts.widget);   // verify again for the next call
    }
    updateGate();
  }

  function mountTurnstile() {
    if (!cfg.turnstileSiteKey) return;
    waitForTurnstile().then(function (t) {
      var box = $(".ds-turnstile");
      if (!t || !box) { ts.unavailable = true; ts.waiters.splice(0).forEach(function (w) { clearTimeout(w.timer); w.resolve(""); }); updateGate(); return; }
      ts.widget = t.render(box, {
        sitekey: cfg.turnstileSiteKey, theme: "light", appearance: "always",
        size: window.innerWidth < 360 ? "compact" : "flexible",
        callback: function (token) { gotToken(token); },
        "expired-callback": function () { ts.token = null; updateGate(); },
        "timeout-callback": function () { ts.token = null; updateGate(); },
        "error-callback": function () { ts.token = null; updateGate(); return true; },
        "before-interactive-callback": function () { humanHint(true); },
        "after-interactive-callback": function () { humanHint(false); }
      });
    });
  }

  function getToken() {
    if (!cfg.turnstileSiteKey) return Promise.resolve("preview"); // local preview harness only
    if (ts.unavailable) return Promise.resolve("");
    if (ts.token) {
      var token = ts.token;
      ts.token = null;
      if (ts.widget !== null && window.turnstile) window.turnstile.reset(ts.widget);     // verify again for the next call
      updateGate();
      return Promise.resolve(token);
    }
    return new Promise(function (resolve) {           // not verified yet (or re-verifying): wait for the box
      var w = { resolve: resolve, timer: null };
      w.timer = setTimeout(function () {
        var i = ts.waiters.indexOf(w); if (i >= 0) ts.waiters.splice(i, 1);
        resolve("");
      }, 90000);
      ts.waiters.push(w);
    });
  }

  // The box sometimes needs a click; point at it (the wait for it happens in the box itself).
  function humanHint(on) {
    var box = $(".ds-turnstile");
    if (box) box.classList.toggle("is-asking", !!on);
    var line = $(".ds-loading__line");
    if (on && line) line.textContent = "Please tick the box above the Create button to confirm you're human";
    if (on && box && box.scrollIntoView) box.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Create pressed before the box is verified: say so and point at it (briefly ringed).
  function askToVerify() {
    var box = $(".ds-turnstile");
    if (box) {
      box.classList.add("is-asking");
      setTimeout(function () { if (!ts.waiters.length) box.classList.remove("is-asking"); }, 3000);
      if (box.scrollIntoView) box.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    showNotice("Please tick the box to confirm you're human, then press Create.", true);
    var n = $(".ds-notice"); if (n) n.setAttribute("data-verify", "1");      // cleared as soon as the box verifies
  }

  // ------------------------------------------------------------------ layout

  // The wall block copies the editors' "Enter Your Dimensions" card (custom wallpaper + $65 kit): the How to measure
  // link, the Units pill toggle, Width / Height rows of bordered boxes, and a Note. Inches is the default unit.
  function dimInput(id, unit, max, step, label) {
    return [
      el("input", { id: id, type: "number", class: "ds-dim-input", min: "0", max: String(max), step: step, placeholder: "0",
                    inputmode: step === "1" ? "numeric" : "decimal", "aria-label": label }),
      el("span", { class: "ds-dim-unit", "aria-hidden": "true", text: unit })
    ];
  }

  function dimRow(label, key, maxIn, maxFt) {
    return el("div", { class: "ds-dim-group" }, [
      el("span", { class: "ds-dim-label", "aria-hidden": "true", text: label }),
      el("div", { class: "ds-dim-row", "data-unit": "in" }, dimInput("ds-" + key + "-in", "in", maxIn, "0.1", label + " in inches")),
      el("div", { class: "ds-dim-row", "data-unit": "ftin", hidden: true },
        dimInput("ds-" + key + "-ft", "ft", maxFt, "1", label + ", feet")
          .concat(dimInput("ds-" + key + "-fi", "in", 11.9, "0.1", label + ", inches")))
    ]);
  }

  function arrow() {
    var s = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    s.setAttribute("width", "18"); s.setAttribute("height", "10"); s.setAttribute("viewBox", "0 0 18 10");
    s.setAttribute("aria-hidden", "true"); s.setAttribute("class", "ds-arrow");
    s.innerHTML = '<path d="M0 5h17M13 1l4 4-4 4" fill="none" stroke="currentColor"></path>';
    return s;
  }

  // "12′ 0″ × 8′ 6″" from a {width_in, height_in} wall (feet + inches, per the house style)
  function fmtWall(wall) {
    if (!wall || !wall.width_in || !wall.height_in) return "";
    var f = function (inches) { var t = Math.round(inches); return Math.floor(t / 12) + "′ " + (t % 12) + "″"; };
    return f(wall.width_in) + " × " + f(wall.height_in);
  }

  function setUnit(unit) {
    if (unit === state.unit) return;
    var n = function (id) { var v = parseFloat(($("#" + id) || {}).value); return isNaN(v) || v < 0 ? 0 : v; };
    ["w", "h"].forEach(function (k) {
      var total = state.unit === "in" ? n("ds-" + k + "-in") : n("ds-" + k + "-ft") * 12 + n("ds-" + k + "-fi");
      var set = function (id, v) { $("#" + id).value = total ? String(v) : ""; };
      if (unit === "in") set("ds-" + k + "-in", Math.round(total * 10) / 10);
      else { set("ds-" + k + "-ft", Math.floor(total / 12)); set("ds-" + k + "-fi", Math.round((total % 12) * 10) / 10); }
    });
    state.unit = unit;
    root.querySelectorAll(".ds-dim-row").forEach(function (r) { r.hidden = r.getAttribute("data-unit") !== unit; });
    root.querySelectorAll(".ds-unit-btn").forEach(function (b) {
      var on = b.getAttribute("data-unit") === unit;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    });
  }

  // The editors' measuring guide: the theme snippet wallpaper-measure-walls-modal, rendered by the section.
  function openMeasureGuide() {
    var modal = document.getElementById("measure-walls-modal");
    if (!modal) return;
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    modal.style.display = "flex";
    modal.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  function wallBlock() {
    return el("div", { class: "ds-dims", role: "group", "aria-labelledby": "ds-dims-title" }, [
      el("h3", { class: "ds-dims__title", id: "ds-dims-title", text: "Your Wall Dimensions" }),
      el("button", { type: "button", class: "ds-measure", onclick: openMeasureGuide }, [
        svgInfo(), el("span", { text: "How to measure your walls" })
      ]),
      el("div", { class: "ds-units" }, [
        el("span", { class: "ds-units__label", text: "Units" }),
        el("div", { class: "ds-units__toggle", role: "group", "aria-label": "Measurement units" }, [
          el("button", { type: "button", class: "ds-unit-btn is-active", "data-unit": "in", "aria-pressed": "true", text: "Inches",
                         onclick: function () { setUnit("in"); } }),
          el("button", { type: "button", class: "ds-unit-btn", "data-unit": "ftin", "aria-pressed": "false", text: "Ft + In",
                         onclick: function () { setUnit("ftin"); } })
        ])
      ]),
      dimRow("Width", "w", 1200, 100),
      dimRow("Height", "h", 300, 25),
      el("small", { class: "ds-dims__note" }, [
        el("strong", { text: "Note:" }), " Measure the whole wall. Not sure yet? Leave it blank and we'll use a standard shape."
      ]),
      el("p", { class: "ds-error", id: "ds-wall-error", role: "alert", hidden: true })
    ]);
  }

  function svgInfo() {
    var ns = "http://www.w3.org/2000/svg";
    var s = document.createElementNS(ns, "svg");
    s.setAttribute("width", "12"); s.setAttribute("height", "12"); s.setAttribute("viewBox", "0 0 16 16");
    s.setAttribute("aria-hidden", "true");
    s.innerHTML = '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5"></circle>' +
                  '<text x="8" y="11" text-anchor="middle" font-size="10" fill="currentColor">i</text>';
    return s;
  }

  // The empty canvas takes the wall's shape as it's typed (clamped to the range designs are made in).
  function placeholderAspect() {
    var w = readWallInches();
    if (!w.w || !w.h) return 1.5;
    return Math.min(21 / 9, Math.max(0.5, w.w / w.h));
  }
  function reshapeEmpty() {
    var box = $(".ds-empty");
    if (!box) return;
    box.style.aspectRatio = placeholderAspect().toFixed(4);
    var w = readWallInches();
    var ft = function (i) { var t = Math.round(i); return Math.floor(t / 12) + "′ " + (t % 12) + "″"; };
    var lw = box.querySelector(".ds-rule--w .ds-rule__label"), lh = box.querySelector(".ds-rule--h .ds-rule__label");
    if (lw) lw.textContent = w.w ? ft(w.w) : "Width";
    if (lh) lh.textContent = w.h ? ft(w.h) : "Height";
    // The 5' 8" figure is drawn to the wall's scale (an 8' wall when none is typed yet).
    var person = box.querySelector(".ds-scale");
    if (person) person.style.height = Math.min(100, 68 / (w.h || 96) * 100).toFixed(2) + "%";
  }

  function personSvg() {
    var s = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    s.setAttribute("viewBox", "0 0 44 170"); s.setAttribute("class", "ds-person"); s.setAttribute("aria-hidden", "true");
    s.setAttribute("preserveAspectRatio", "xMinYMax meet");
    s.innerHTML = '<circle cx="22" cy="12" r="10" fill="currentColor"></circle>' +
      '<path d="M10 28Q22 24 34 28L38 88Q38 92 34 92L32 92 30 166 24 166 22 104 20 166 14 166 12 92 10 92Q6 92 6 88Z" fill="currentColor"></path>';
    return s;
  }

  // "Ideas to start from": the section's Idea blocks (a picture + a prompt) or the text-only defaults. A click
  // fills the prompt; nothing is generated until the shopper presses Create.
  function ideasRow() {
    var ideas = (cfg.ideas && cfg.ideas.length ? cfg.ideas : DEFAULT_IDEAS.map(function (t) { return { prompt: t }; }))
      .filter(function (x) { return x && x.prompt; }).slice(0, 4);
    if (!ideas.length) return null;
    var withPics = ideas.some(function (x) { return x.image; });
    return el("div", { class: "ds-ideas" + (withPics ? "" : " is-text") }, [
      el("p", { class: "ds-eyebrow", id: "ds-ideas-title", text: "Ideas to start from" }),
      el("ul", { class: "ds-ideas__list", "aria-labelledby": "ds-ideas-title" }, ideas.map(function (x) {
        return el("li", {}, [el("button", { type: "button", class: "ds-idea", onclick: function () {
          var t = $("#ds-prompt");
          t.value = x.prompt.slice(0, 600);
          growPrompt();
          t.focus();
        } }, [
          x.image ? el("img", { src: x.image, alt: "", draggable: "false", loading: "lazy" }) : null,
          el("span", { class: "ds-idea__text", text: x.prompt })
        ])]);
      }))
    ]);
  }

  // The prompt starts at two lines and grows with what's typed (no scrollbar, no resize handle).
  function growPrompt() {
    var t = $("#ds-prompt");
    if (!t) return;
    t.style.height = "auto";
    t.style.height = (t.scrollHeight + t.offsetHeight - t.clientHeight) + "px";   // + the hairline border
  }

  // The result column before any design exists: the wall drawing and the ideas row.
  function emptyResult() {
    return [
      el("div", { class: "ds-empty" }, [
        el("span", { class: "ds-rule ds-rule--w", "aria-hidden": "true" }, [el("span", { class: "ds-rule__label", text: "Width" })]),
        el("span", { class: "ds-rule ds-rule--h", "aria-hidden": "true" }, [el("span", { class: "ds-rule__label", text: "Height" })]),
        el("p", { class: "ds-empty__title", text: "Your design appears here" }),
        el("p", { class: "ds-empty__sub", text: "at your wall's exact size" }),
        el("div", { class: "ds-scale", "aria-hidden": "true" }, [personSvg(), el("span", { class: "ds-scale__cap", text: "5′ 8″ person, for scale" })])
      ]),
      ideasRow()
    ];
  }

  function build() {
    root.innerHTML = "";

    var form = el("div", { class: "ds-form", role: "group", "aria-label": "Describe your mural",
      onkeydown: function (e) { if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); generate("new"); } },
      oninput: function (e) {
        if (e.target.classList.contains("ds-dim-input")) reshapeEmpty();
        if (e.target.id === "ds-prompt") growPrompt();
      } }, [
      el("header", { class: "ds-hero" }, [
        cfg.eyebrow ? el("p", { class: "ds-eyebrow", text: cfg.eyebrow }) : null,
        el("h1", { class: "ds-heading", text: cfg.heading || "Create your own mural" }),
        cfg.intro ? el("p", { class: "ds-intro", text: cfg.intro }) : null
      ]),
      el("div", { class: "ds-field" }, [
        el("label", { class: "ds-eyebrow", for: "ds-prompt", text: "Your mural" }),
        el("textarea", { id: "ds-prompt", class: "ds-textarea", maxlength: "600", rows: "2", placeholder: cfg.placeholder || "" }),
        el("button", { type: "button", class: "ds-measure ds-tips-link", id: "ds-tips-link", "aria-haspopup": "dialog",
                       onclick: openTips }, [svgInfo(), el("span", { text: "Prompt tips" })])
      ]),
      wallBlock(),
      el("div", { class: "ds-turnstile" }),
      el("button", { type: "button", class: "ds-btn ds-btn--primary ds-create", onclick: function () { generate("new"); } }, [
        el("span", { text: "Create my design" }), arrow()
      ]),
      el("p", { class: "ds-free", "aria-live": "polite" })
    ]);

    var result = el("section", { class: "ds-result", "aria-live": "polite", "aria-label": "Your design" }, emptyResult());

    root.appendChild(el("p", { class: "ds-account", "aria-live": "polite" }));
    root.appendChild(el("div", { class: "ds-grid" }, [form, result]));
    root.appendChild(el("section", { class: "ds-history", hidden: true }, [
      el("h2", { class: "ds-history__title", text: "Your designs" }),
      el("div", { class: "ds-history__list" }),
      el("p", { class: "ds-history__note" })
    ]));
    root.appendChild(el("p", { class: "ds-disclosure" }, [
      cfg.disclosure || "",
      cfg.termsUrl ? " " : null,
      cfg.termsUrl ? el("a", { href: cfg.termsUrl, text: "Studio terms" }) : null
    ]));

    var idea = params.get("idea");
    if (idea) { $("#ds-prompt").value = idea.slice(0, 600); growPrompt(); }
    reshapeEmpty();
    updateGate();
    mountTurnstile();
    root.addEventListener("contextmenu", function (e) { if (e.target && e.target.tagName === "IMG") e.preventDefault(); });
  }

  function renderResting(reason) {
    state.resting = true;
    var grid = $(".ds-grid");
    if (!grid) return;
    grid.innerHTML = "";
    grid.classList.add("is-resting");
    grid.appendChild(el("div", { class: "ds-resting" }, [
      cfg.eyebrow ? el("p", { class: "ds-eyebrow", text: cfg.eyebrow }) : null,
      el("h1", { class: "ds-heading", text: cfg.heading || "Create your own mural" }),
      el("h2", { class: "ds-subheading", text: reason === "pacing" ? "The studio is resting until tomorrow" : (cfg.restingTitle || "The studio is resting right now") }),
      el("p", { text: cfg.restingText || "" }),
      el("ul", { class: "ds-links" }, [
        el("li", {}, [el("a", { href: cfg.uploadUrl, class: "ds-btn ds-btn--primary", text: "Upload your own image" })]),
        el("li", {}, [el("a", { href: cfg.kitUrl, class: "ds-btn", text: "Order a custom design sample" })]),
        el("li", {}, [el("a", { href: "mailto:info@appliedcoverings.com", class: "ds-textlink", text: "Email info@appliedcoverings.com" })])
      ])
    ]));
  }

  // ------------------------------------------------------------------ sign in

  // Shopify's sign-in, back to this page. ?preview and ?idea are dropped: the preview code is already stored,
  // and a ?preview on the way back would clear this browser's session before its designs move to the account.
  function signInUrl() {
    var q = new URLSearchParams(window.location.search);
    q.delete("preview"); q.delete("idea");
    var back = window.location.pathname + (q.toString() ? "?" + q.toString() : "");
    return (cfg.loginUrl || "/customer_authentication/login") + "?return_to=" + encodeURIComponent(back);
  }

  function signInLink(text) {
    return el("a", { class: "ds-textlink", href: signInUrl(), text: text,
      onclick: function () { this.href = signInUrl(); } });
  }

  function renderAccount() {
    var p = $(".ds-account");
    if (p) {
      p.innerHTML = "";
      if (state.signedIn) {
        p.appendChild(document.createTextNode("Signed in. Your designs are saved to your account. "));
        if (cfg.logoutUrl) p.appendChild(el("a", { class: "ds-textlink", href: cfg.logoutUrl, text: "Sign out" }));
      } else {
        p.appendChild(document.createTextNode("Save your designs and see them on any device. "));
        p.appendChild(signInLink("Sign in"));
      }
    }
    var note = $(".ds-history__note");
    if (note) {
      note.innerHTML = "";
      if (!state.signedIn) {
        note.appendChild(document.createTextNode("Saved on this device only. "));
        note.appendChild(signInLink("Sign in to keep them on any device"));
      }
    }
  }

  function updateFree() {
    var p = $(".ds-free");
    if (!p) return;
    if (state.freeLeft === null || state.freeLeft === undefined) { p.textContent = state.hasEmail ? "" : "3 free designs · no email needed"; return; }
    p.textContent = state.freeLeft > 0
      ? state.freeLeft + " free design" + (state.freeLeft === 1 ? "" : "s") + " left · no email needed"
      : "Add your email or sign in to keep designing.";
  }

  function applySession(data) {
    if (!data) return;
    if ("free_left" in data) state.freeLeft = data.free_left;
    if ("has_email" in data) state.hasEmail = !!data.has_email;
    if ("signed_in" in data) state.signedIn = !!data.signed_in;
    updateFree();
    renderAccount();
  }

  function setBusy(on) {
    state.busy = on;
    root.querySelectorAll("button, textarea, input").forEach(function (b) {
      if (b.closest(".ds-modal")) return;
      if (on) { b.dataset.dsWasDisabled = b.disabled ? "1" : ""; b.disabled = true; }
      else if (b.dataset.dsWasDisabled !== undefined) { b.disabled = b.dataset.dsWasDisabled === "1"; delete b.dataset.dsWasDisabled; }
    });
    root.classList.toggle("is-busy", on);
  }

  // ------------------------------------------------------------------ wall input

  function readWallInches() {
    var n = function (id) { var v = parseFloat(($("#" + id) || {}).value); return isNaN(v) || v < 0 ? 0 : v; };
    if (state.unit === "in") return { w: n("ds-w-in"), h: n("ds-h-in") };
    return { w: n("ds-w-ft") * 12 + n("ds-w-fi"), h: n("ds-h-ft") * 12 + n("ds-h-fi") };
  }

  function readWall() {
    var total = readWallInches(), w = total.w, h = total.h;
    var err = $("#ds-wall-error");
    err.hidden = true;
    if (!w && !h) return { ok: true, wall: null };
    if (!w || !h) return fail("Enter both the width and the height, or leave both blank.");
    if (w < 12 || h < 12) return fail("The wall needs to be at least 1 ft wide and 1 ft high.");
    if (w > 1200 || h > 300) return fail("For walls wider than 100 ft or taller than 25 ft, email info@appliedcoverings.com and we'll help.");
    return { ok: true, wall: { width_in: w, height_in: h } };
    function fail(msg) { err.textContent = msg; err.hidden = false; return { ok: false }; }
  }

  // ------------------------------------------------------------------ session

  function ensureSession() {
    var check = state.session ? api("GET", "/api/session") : Promise.resolve({ status: 401 });
    return check.then(function (r) {
      if (r.status === 200) { applySession(r.data); return true; }
      state.session = null; lsDel(LS_SESSION);
      return getToken().then(function (token) {
        var body = { turnstile: token || "none" };
        if (state.previewCode) body.preview_code = state.previewCode;
        return api("POST", "/api/session", body);
      }).then(function (r2) {
        if (r2.status === 200 && r2.data.session) {
          state.session = r2.data.session; lsSet(LS_SESSION, state.session); applySession(r2.data);
          if (r2.data.state === "resting") { renderResting(r2.data.reason); return false; }
          return true;
        }
        showNotice(r2.status === 429 ? LIMIT_TEXT.limit_ip : r2.status === 403
          ? "We couldn't confirm you're a person. Please refresh the page and try again."
          : "We couldn't reach the studio. Please try again.", false, r2.status === 429);
        return false;
      });
    });
  }

  // ------------------------------------------------------------------ generate

  var loadingTimer = null;
  function showLoading(kind) {
    var result = $(".ds-result");
    result.innerHTML = "";
    var line = el("p", { class: "ds-loading__line", text: kind === "retry" ? "Making another take" : LOADING_LINES[0] });
    var box = el("div", { class: "ds-loading" }, [el("div", { class: "ds-spinner", "aria-hidden": "true" }), line,
      el("p", { class: "ds-hint", text: "This usually takes about 10 seconds." })]);
    box.style.aspectRatio = (kind !== "new" && state.current && state.current.aspect ? state.current.aspect : placeholderAspect()).toFixed(4);
    result.appendChild(box);
    revealResult();
    var i = 0;
    clearInterval(loadingTimer);
    loadingTimer = setInterval(function () { i = Math.min(i + 1, LOADING_LINES.length - 1); line.textContent = LOADING_LINES[i]; }, 2600);
  }
  function stopLoading() { clearInterval(loadingTimer); loadingTimer = null; }

  // One column on phones puts the result under the Create button, so bring it into view.
  function revealResult() {
    var result = $(".ds-result");
    if (!result || !window.matchMedia || !window.matchMedia("(max-width: 860px)").matches) return;
    var top = result.getBoundingClientRect().top;
    if (top > window.innerHeight * 0.6 || top < 0) result.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // withAlt: a daily limit was hit, so offer somewhere else to go instead of a dead end.
  function showNotice(text, keepDesign, withAlt) {
    stopLoading();
    var result = $(".ds-result");
    if (!keepDesign) result.innerHTML = "";
    var old = result.querySelector(".ds-notice");
    if (old) old.remove();
    var kids = [text];
    if (withAlt) kids.push(el("span", { class: "ds-notice__alt" }, [
      "In the meantime, you can ", el("a", { href: cfg.uploadUrl, text: "upload your own image" }),
      " or ", el("a", { href: "/collections/shop-all-wall-murals", text: "browse our murals" }), "."
    ]));
    result.insertBefore(el("p", { class: "ds-notice", role: "alert" }, kids), result.firstChild);
  }

  function generate(kind, opts) {
    if (state.busy || state.resting) return;
    opts = opts || {};
    if (kind === "new" && !turnstileReady()) { askToVerify(); return; }
    var prompt = kind === "change" ? (opts.change || "") : ($("#ds-prompt").value || "").trim();
    if (kind === "new" && prompt.length < 3) {
      showNotice("Tell us a little about the mural you'd like, for example a place, a mood or a style.");
      $("#ds-prompt").focus();
      return;
    }
    var wallRead = readWall();
    if (!wallRead.ok) return;
    setBusy(true);
    showLoading(kind);
    ensureSession().then(function (ok) {
      if (!ok) { setBusy(false); return null; }
      return getToken().then(function (token) {
        var body = { kind: kind, mode: "mural", prompt: prompt, turnstile: token || "none" };
        if (wallRead.wall) body.wall = wallRead.wall;
        else if (kind !== "new" && state.current && state.current._wall) body.wall = state.current._wall;
        if (kind !== "new") body.parent_design_id = state.current && state.current.id;
        return api("POST", "/api/generate", body);
      });
    }).then(function (r) {
      if (!r) return;
      var d = r.data || {};
      if (r.status === 202 && d.job_id) return poll(d.job_id, wallRead.wall);
      setBusy(false);
      if (d.state === "email_required") { restoreCurrent(); openEmail(function () { generate(kind, opts); }); return; }
      if (d.state === "resting") { renderResting(d.reason); return; }
      if (r.status === 403) { showNotice("We couldn't confirm you're a person. Please try again."); return; }
      if (r.status === 429 && d.reason === "one_at_a_time") { showNotice(LIMIT_TEXT.one_at_a_time); return; }
      if (d.state === "limited") { restoreCurrent(); showNotice(LIMIT_TEXT[d.reason] || LIMIT_TEXT.busy, true, d.reason === "limit_ip" || d.reason === "limit_email_day"); return; }
      if (r.status === 401) { state.session = null; lsDel(LS_SESSION); showNotice(LIMIT_TEXT.no_session); return; }
      if (r.status === 400) { showNotice(typeof d.detail === "string" ? friendly400(d.detail) : "Please check your description and wall size."); return; }
      showNotice(d.message || "We couldn't reach the studio. Please try again.");
    });
  }

  function friendly400(detail) {
    if (detail === "describe the mural") return "Tell us a little about the mural you'd like.";
    if (detail === "describe the change") return "Tell us what to change, for example \"warmer colours\".";
    if (detail === "unknown parent design") return "That design is no longer available. Start a new one.";
    return "Please check your description and wall size.";
  }

  function poll(jobId, wall) {
    var started = Date.now();
    function step() {
      return api("GET", "/api/jobs/" + encodeURIComponent(jobId)).then(function (r) {
        var d = r.data || {};
        if (r.status === 200 && (d.state === "queued" || d.state === "running")) {
          if (Date.now() - started > 120000) { setBusy(false); showNotice("This is taking longer than usual. Please try again."); return; }
          return sleep(1500).then(step);
        }
        setBusy(false);
        stopLoading();
        if (d.session) applySession(d.session);
        if (d.state === "done" && d.design) {
          var design = d.design;
          design._wall = wall || (state.current && state.current._wall) || null;
          addToHistory(design);
          showDesign(design);
          return;
        }
        if (d.state === "refused") { restoreCurrent(); showRefused(d.message, d.pattern_request); return; }
        restoreCurrent();
        showNotice(d.message || "That one didn't come out right, so we didn't count it. Please try again.", !!state.current);
      });
    }
    return step();
  }

  // After a limit or the email step interrupts a generate: put back the design on screen, or, on a first design,
  // the empty wall drawing (otherwise the "Sketching your idea" spinner is left running under the message).
  function restoreCurrent() {
    if (state.current) { showDesign(state.current); return; }
    stopLoading();
    var result = $(".ds-result");
    result.innerHTML = "";
    emptyResult().forEach(function (n) { if (n) result.appendChild(n); });
    reshapeEmpty();
  }

  function showRefused(message, patternRequest) {
    var result = $(".ds-result");
    var box = el("div", { class: "ds-refused" }, [
      el("p", { class: "ds-refused__msg", text: message || "We can't make that one. Try describing a place, a mood or a style." })
    ]);
    if (patternRequest) box.appendChild(patternNote());
    if (!state.current) result.innerHTML = "";
    var old = result.querySelector(".ds-refused");
    if (old) old.remove();
    result.insertBefore(box, result.firstChild);
    $("#ds-prompt").focus();
  }

  function patternNote() {
    return el("p", { class: "ds-pattern-note" }, [
      (cfg.patternsNote || "") + " ",
      el("a", { href: "/collections/all-wallpaper", text: "Browse patterns" }), " · ",
      el("a", { href: cfg.uploadUrl, text: "Upload your own" })
    ]);
  }

  // ------------------------------------------------------------------ the design

  function showDesign(d) {
    state.current = d;
    var result = $(".ds-result");
    result.innerHTML = "";
    var ratio = d.aspect || 1.5;
    var img = el("img", { src: d.card_url, alt: d.name + ", AI-generated mural design preview", draggable: "false",
                          class: "ds-stage__img", decoding: "async" });
    var stage = el("figure", { class: "ds-stage" + (d.very_wide ? " is-strip" : "") }, [img]);
    stage.style.aspectRatio = ratio.toFixed(4);
    result.appendChild(stage);

    if (d.very_wide && d.strip) {
      var h = d.strip.h;
      var travel = Math.max(0.0001, 1 - h);
      if (state.stripOffset[d.id] === undefined) state.stripOffset[d.id] = Math.min(1, Math.max(0, d.strip.y / travel));
      var top = el("div", { class: "ds-shade ds-shade--top" });
      var bottom = el("div", { class: "ds-shade ds-shade--bottom" });
      var frame = el("div", { class: "ds-strip-frame" });
      stage.appendChild(top); stage.appendChild(bottom); stage.appendChild(frame);
      var place = function () {
        var y = state.stripOffset[d.id] * travel;
        top.style.height = (y * 100) + "%";
        bottom.style.height = ((1 - y - h) * 100) + "%";
        frame.style.top = (y * 100) + "%";
        frame.style.height = (h * 100) + "%";
      };
      place();
      result.appendChild(el("label", { class: "ds-strip-control" }, [
        el("span", { text: "Your wall is extra wide, so we'll print the band inside the frame. Slide to choose which part." }),
        el("input", { type: "range", min: "0", max: "1000", value: String(Math.round(state.stripOffset[d.id] * 1000)),
          "aria-label": "Move the band up or down",
          oninput: function (e) { state.stripOffset[d.id] = e.target.value / 1000; place(); } })
      ]));
    }

    var size = fmtWall(d._wall);
    result.appendChild(el("div", { class: "ds-caption" }, [
      el("h2", { class: "ds-design-name", tabindex: "-1", text: d.name }),
      size ? el("span", { class: "ds-caption__size", text: "Mural · " + size }) : null
    ]));
    if (d.message) result.appendChild(el("p", { class: "ds-design-msg", text: d.message }));
    if (d.pattern_request) result.appendChild(patternNote());

    var submitChange = function () {
      var v = (changeBox.querySelector("input").value || "").trim();
      if (v.length < 2) { changeBox.querySelector("input").focus(); return; }
      generate("change", { change: v });
    };
    var changeBox = el("div", { class: "ds-change", hidden: true,
      onkeydown: function (e) { if (e.key === "Enter") { e.preventDefault(); submitChange(); } } }, [
      el("label", { class: "ds-eyebrow", for: "ds-change-input", text: "What should we change?" }),
      el("div", { class: "ds-change__row" }, [
        el("input", { id: "ds-change-input", type: "text", maxlength: "300", placeholder: "e.g. warmer colours, less fog, add birds" }),
        el("button", { type: "button", class: "ds-btn ds-btn--primary", text: "Update design", onclick: submitChange })
      ])
    ]);

    result.appendChild(el("div", { class: "ds-actions" }, [
      el("button", { type: "button", class: "ds-btn ds-btn--primary", text: "Use this design", onclick: function () { useDesign("editor"); } }),
      el("button", { type: "button", class: "ds-btn", text: "Order a 24″ sample", onclick: function () { useDesign("kit"); } }),
      el("span", { class: "ds-textlinks" }, [
        el("button", { type: "button", class: "ds-textlink", text: "Try again", onclick: function () { generate("retry"); } }),
        el("button", { type: "button", class: "ds-textlink", text: "Change it", onclick: function () {
          changeBox.hidden = !changeBox.hidden; if (!changeBox.hidden) changeBox.querySelector("input").focus();
        } })
      ])
    ]));
    result.appendChild(changeBox);
    result.appendChild(el("p", { class: "ds-hint", text: "\"Use this design\" opens our wallpaper editor with this design, where you pick the material and add it to your cart. The sample prints a 24″ × 24″ piece of it first." }));
    var heading = result.querySelector(".ds-design-name");
    if (heading && heading.focus) heading.focus({ preventScroll: true });
    markHistory();
    revealResult();
  }

  function useDesign(target) {
    var d = state.current;
    if (!d || state.busy) return;
    setBusy(true);
    var actions = $(".ds-actions");
    var status = el("p", { class: "ds-checking", role: "status", text: target === "kit" ? "Setting up your 24″ sample…" : "Opening it in our wallpaper editor…" });
    if (actions) actions.parentNode.insertBefore(status, actions.nextSibling);
    var body = { target: target };
    if (d.very_wide && state.stripOffset[d.id] !== undefined) body.strip_offset = state.stripOffset[d.id];
    // a design from the account on a new device: this browser has no session yet, so make one first
    ensureSession().then(function (ok) {
      if (!ok) return null;
      return api("POST", "/api/designs/" + encodeURIComponent(d.id) + "/select", body);
    }).then(function (r) {
      if (!r) { setBusy(false); status.remove(); return; }
      var out = r.data || {};
      if (out.state === "ok" && out.url) { window.location.href = out.url; return; }
      setBusy(false);
      status.remove();
      if (out.state === "rejected") {
        state.designs = state.designs.filter(function (x) { return x.id !== d.id; });
        renderHistory();
        state.current = null;
        var result = $(".ds-result");
        result.innerHTML = "";
        result.appendChild(el("div", { class: "ds-refused" }, [
          el("p", { class: "ds-refused__msg", text: out.message }),
          el("div", { class: "ds-actions" }, [
            el("button", { type: "button", class: "ds-btn ds-btn--primary", text: "Make a fresh one", onclick: function () { generate("new"); } })
          ])
        ]));
        return;
      }
      if (out.state === "resting") { renderResting(out.reason); return; }
      showNotice(out.message || "Something went wrong on our side. Please try again.", true);
    });
  }

  // ------------------------------------------------------------------ your designs

  function addToHistory(d) {
    state.designs = state.designs.filter(function (x) { return x.id !== d.id; });
    state.designs.push(d);
    renderHistory();
  }

  function renderHistory() {
    var wrap = $(".ds-history");
    var list = $(".ds-history__list");
    if (!wrap || !list) return;
    list.innerHTML = "";
    wrap.hidden = state.designs.length < 1;
    state.designs.slice().reverse().forEach(function (d) {
      list.appendChild(el("button", { type: "button", class: "ds-thumb", "data-id": d.id, title: d.name,
        onclick: function () { if (!state.busy) showDesign(d); } }, [
        el("img", { src: d.card_url, alt: d.name, draggable: "false", loading: "lazy" }),
        el("span", { class: "ds-thumb__name", text: d.name })
      ]));
    });
    markHistory();
  }

  function markHistory() {
    root.querySelectorAll(".ds-thumb").forEach(function (b) {
      b.setAttribute("aria-current", state.current && b.getAttribute("data-id") === state.current.id ? "true" : "false");
    });
  }

  // ------------------------------------------------------------------ email step

  function openEmail(onDone) {
    var close = function () { modal.remove(); document.removeEventListener("keydown", onKey); };
    var onKey = function (e) { if (e.key === "Escape") close(); };
    var err = el("p", { class: "ds-error", role: "alert", hidden: true });
    var input = el("input", { type: "email", id: "ds-email", required: true, autocomplete: "email", placeholder: "you@example.com" });
    var consent = el("input", { type: "checkbox", id: "ds-consent" });
    var submit = el("button", { type: "button", class: "ds-btn ds-btn--primary", text: "Keep designing", onclick: function () { send(); } });

    var send = function () {
        var email = (input.value || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { err.textContent = "Please enter a valid email address."; err.hidden = false; return; }
        submit.disabled = true;
        api("POST", "/api/email", { email: email, consent: !!consent.checked }).then(function (r) {
          submit.disabled = false;
          if (r.status === 200) { applySession(r.data); close(); if (onDone) onDone(); return; }
          err.textContent = r.data && r.data.reason === "disposable" ? "Please use a permanent email address."
            : "We couldn't use that email address. Please check it and try again.";
          err.hidden = false;
        });
    };
    var modal = el("div", { class: "ds-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "ds-email-title" }, [
      el("div", { class: "ds-modal__card",
        onkeydown: function (e) { if (e.key === "Enter" && e.target.type === "email") { e.preventDefault(); send(); } } }, [
        el("h2", { class: "ds-subheading", id: "ds-email-title", text: "Keep designing" }),
        el("p", { text: "You've used your free designs. Add your email to keep going. We'll also keep your designs on this device so you can come back to them." }),
        el("p", { class: "ds-hint" }, ["Or ", signInLink("sign in instead"), " and your designs are saved to your account."]),
        el("label", { class: "ds-eyebrow", for: "ds-email", text: "Email" }), input,
        el("label", { class: "ds-check", for: "ds-consent" }, [consent, el("span", { text: "Email me design ideas and offers from Applied Coverings. Unsubscribe anytime." })]),
        err,
        el("div", { class: "ds-actions" }, [submit, el("button", { type: "button", class: "ds-textlink", text: "Not now", onclick: close })])
      ])
    ]);
    document.body.appendChild(modal);
    document.addEventListener("keydown", onKey);
    input.focus();
  }

  // ------------------------------------------------------------------ prompt tips (text only)

  function openTips() {
    var trigger = document.getElementById("ds-tips-link");
    var close = function () {
      modal.remove(); document.removeEventListener("keydown", onKey);
      if (trigger) trigger.focus();
    };
    var onKey = function (e) { if (e.key === "Escape") close(); };
    var tip = function (lead, rest) { return el("li", {}, [el("strong", { text: lead }), " " + rest]); };
    var modal = el("div", { class: "ds-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "ds-tips-title",
                            onclick: function (e) { if (e.target === modal) close(); } }, [
      el("div", { class: "ds-modal__card ds-modal__card--tips" }, [
        el("h2", { class: "ds-subheading", id: "ds-tips-title", text: "Getting a great design" }),
        el("ol", { class: "ds-tips" }, [
          tip("Start with the subject, then the style.", "For example, “A misty pine forest at dawn, in soft watercolour.” Name a medium (watercolour, oil painting, linocut, photograph) so the look is clear."),
          tip("Name your colours.", "Two to four is plenty, like “sage, cream and warm gold.” You can give an exact colour too, such as deep teal #1F5C63."),
          tip("Say what you want to see, not what to avoid.", "“A clear sky” works better than “no clouds”; naming the thing you don’t want tends to add it."),
          tip("Set the mood and the light.", "Golden hour, moody and quiet, bright and airy."),
          tip("Be specific.", "“Three herons standing in shallow water” beats “birds.”"),
          tip("One design at a time.", "Use Try again for a fresh take on the same idea, or Change it to adjust one thing, like “make the sky warmer.”")
        ]),
        el("h3", { class: "ds-tips__sub", text: "What we can’t make" }),
        el("ul", { class: "ds-tips ds-tips--plain" }, [
          el("li", { text: "Brand logos, characters or real people." }),
          el("li", { text: "Words, names or lettering." }),
          el("li", {}, ["Repeating patterns. The studio makes one large mural. For a repeat, ",
            el("a", { href: "/collections/all-wallpaper", text: "browse our patterns" }), " or ",
            el("a", { href: cfg.uploadUrl, text: "upload your own design" }), "."])
        ]),
        el("p", { class: "ds-hint", text: "The studio is in beta, so results can vary. Not sure where to start? Pick one from “Ideas to start from.”" }),
        el("div", { class: "ds-actions" }, [el("button", { type: "button", class: "ds-btn ds-btn--primary", id: "ds-tips-close", text: "Got it", onclick: close })])
      ])
    ]);
    document.body.appendChild(modal);
    document.addEventListener("keydown", onKey);
    var btn = document.getElementById("ds-tips-close"); if (btn) btn.focus();
  }

  // ------------------------------------------------------------------ start

  function start() {
    build();
    updateFree();
    renderAccount();
    if (!cfg.enabled) { renderResting(); return; }
    api("GET", "/api/status").then(function (r) {
      if (r.status !== 200) { renderResting(); return; }
      state.signedIn = !!r.data.signed_in;
      renderAccount();
      if (r.data.session) applySession(r.data.session);
      else if (state.session) { state.session = null; lsDel(LS_SESSION); }
      // A preview code only matters on a fresh session; keep the page open so it can be used
      if (r.data.state === "resting" && !(state.previewCode && !r.data.session)) { renderResting(r.data.reason); return; }
      if (state.session || state.signedIn) {           // signed in on a new device: the account's designs
        api("GET", "/api/designs").then(function (d) {
          if (d.status === 200 && d.data.designs && d.data.designs.length) {
            state.designs = d.data.designs.filter(function (x) { return x.status !== "rejected"; });
            renderHistory();
          }
        });
      }
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
